import type {
  ActionListFullValue,
  ActionListValue,
} from '@latitude/action-list';
import type { SelectProps } from '@latitude/select';
import { type AsyncSelectProps, type SyncSelectProps } from '@latitude/select';
import type { BasicFormProps } from 'components/types';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';

// Distributive Omit that preserves union members (standard Omit collapses unions)
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

export type FormSelectProps<
  isMulti extends boolean = false,
  isNullable extends boolean = false,
  ExtraData = never,
  T extends FieldValues = FieldValues,
> = BasicFormProps<T> &
  DistributiveOmit<
    SelectProps<isMulti, isNullable, ExtraData>,
    'onChange' | 'value'
  > & {
    onChange?: (
      value: ActionListValue<isMulti, isNullable>,
      fullValue: ActionListFullValue<isMulti, isNullable, ExtraData>,
      formOnChange: ControllerRenderProps['onChange'],
    ) => void;
  };

// Narrowed prop types for the async/sync branches in FormSelect.
// SelectProps is a discriminated union — TypeScript collapses unions
// when spread into JSX. These types narrow selectProps + onChange
// per mode so each <Select> call is type-safe.

export type AsyncNarrowedProps<isNullable extends boolean, ExtraData> = Omit<
  AsyncSelectProps<isNullable, ExtraData>,
  'onChange' | 'value'
> & {
  onChange: (
    v: ActionListValue<false, isNullable>,
    fv: ActionListFullValue<false, isNullable, ExtraData>,
  ) => void;
};

export type SyncNarrowedProps<
  isMulti extends boolean,
  isNullable extends boolean,
  ExtraData,
> = Omit<
  SyncSelectProps<isMulti, isNullable, ExtraData>,
  'onChange' | 'value'
> & {
  onChange: (
    v: ActionListValue<isMulti, isNullable>,
    fv: ActionListFullValue<isMulti, isNullable, ExtraData>,
  ) => void;
};

// Type guard to narrow selectProps to async mode — keeps the compiler
// in the loop instead of bypassing the union with `as` casts.
export const isAsyncSelectProps = <isNullable extends boolean, ExtraData>(
  p: Record<string, unknown>,
): p is AsyncNarrowedProps<isNullable, ExtraData> =>
  'loadOptions' in p && !!p.loadOptions;
